<template>
  <div>
  <!-- Floating handle to reopen the card after the user closed it -->
  <div
    v-if="showReopenHandle"
    class="results-reopen-handle"
    @click="show()"
    :title="$t('results.show_results')">
    <b-icon icon="chevron-up"></b-icon>
    <span class="results-reopen-label">{{ $t('results.show_results') }}</span>
  </div>

  <div ref="resultsWindow" class="fixed-bottom results-window" :class="{ 'd-none': hideCard, 'h-auto': !cardHeight }" style="z-index: 1000" :style="cardStyle">
    <!-- Drag handle to resize the card vertically -->
    <div
      class="results-resize-handle"
      :class="{ 'resizing': resizing }"
      @mousedown.prevent="startResize"
      @touchstart.prevent="startResize"
      title="Drag to resize">
      <span class="results-resize-grip"></span>
    </div>
    <b-card no-body class="results-card">
      <b-tabs card content-class="mt-3" v-model="activeTabIndex">
        <b-tab :title="$t('results.irrigation_amount')">
          <b-spinner v-if="fetchingResults" variant="primary" label="Spinning"></b-spinner>
          <div v-else-if="!(result && result.length)" class="text-muted p-2">{{ $t('results.no_data_available') }}</div>
          <div v-else v-for="(dayResult, index) in dates" :key="index">
            {{ $d(new Date(dayResult), 'short') }}:
            <span v-if="irrigation[index] !== null">{{ irrigation[index] }} {{ $t(`global.meter_cube_per_hectare`) }}</span>
            <span v-else>{{ $t('results.no_data_available') }}</span>
          </div>
        </b-tab>
        <b-tab :title="$t('results.irrigation_duration')">
          <b-spinner v-if="fetchingResults" variant="primary" label="Spinning"></b-spinner>
          <div v-else-if="!(result && result.length)" class="text-muted p-2">{{ $t('results.no_data_available') }}</div>
          <div v-else v-for="(dayResult, index) in dates" :key="index">
            {{ $d(new Date(dayResult), 'short') }}:
            <span v-if="irrigationDuration[index] === null">{{ $t('results.no_data_available') }}</span>
            <span v-else-if="exceedsDailyLimit(irrigationDuration[index])" class="text-danger">{{ $t('results.exceeds_24h') }}</span>
            <span v-else>{{ irrigationDuration[index] }} {{ $t(`global.hr_per_day`) }}</span>
          </div>
        </b-tab>

        <b-tab :title="$t('results.history_and_forecast')" class="m-0 p-0">
          <b-spinner v-if="fetchingResults" variant="primary" label="Spinning"></b-spinner>
          <div v-else-if="!(result && result.length)" class="text-muted p-2">{{ $t('results.no_data_available') }}</div>
          <div v-else class="min-vw-100" style="direction: ltr;">
            <apexchart ref="chart" width="95%" height="200" :options="options" :series="series" ></apexchart>
          </div>
        </b-tab>

        <b-tab :title="$t('results.mean_fov')" class="m-0 p-0">
          <b-spinner v-if="fetchingResults" variant="primary" label="Spinning"></b-spinner>
          <div v-else-if="!(result && result.length)" class="text-muted p-2">{{ $t('results.no_data_available') }}</div>
          <div v-else class="min-vw-100" style="direction: ltr;">
            <apexchart ref="fovChart" width="95%" height="200" :options="fovOptions" :series="fovSeries" ></apexchart>
          </div>
        </b-tab>

        <b-tab v-if="weatherEnabled" :title="$t('weather.weather')" class="m-0 p-0">
          <WeatherStrip />
        </b-tab>
        <template #tabs-end>
          <b-nav-item :class="$i18n.locale === 'ar' ? 'mr-auto' : 'ml-auto'" role="presentation" @click.prevent="hideManually()">
            <b><b-icon :icon="mobileViewPort ? 'chevron-down' : 'x'"></b-icon></b>
          </b-nav-item>
        </template>
      </b-tabs>
      <template>
        <div class="d-block d-sm-none" @click="displayResults">
          <div class="d-flex justify-content-center">
            <b-icon icon="chevron-down" aria-hidden="true"></b-icon>
          </div>
        </div>
      </template>
    </b-card>
  </div>
  </div>
</template>

<script>
import apexchart from 'vue-apexcharts';
import ApexCharts from 'apexcharts';
import html2canvas from 'html2canvas';
import { mapState, mapGetters } from 'vuex';
import WeatherStrip from './WeatherStrip';
import { WEATHER_ENABLED } from '../../config/features';

// Tab indexes within the results card (order matches the <b-tab> sequence).
const IRRIGATION_TAB_INDEX = 0;
const WEATHER_TAB_INDEX = 4;

export default {
  name: 'ResultsTab',

  props: {
    mobileViewPort: { type: Boolean, default: false },
    field: { type: Object }
  },

  data () {
    return {
      series:  [{
          name: this.$t('results.irrigation_amount'),
          data: []
        }],
        fovSeries: [{
          name: this.$t('results.mean_fov'),
          data: []
        }],
      hideCard: true,
      weatherEnabled: WEATHER_ENABLED,
      activeTabIndex: 0,
      // True only when the user explicitly closed the card (X / chevron), as
      // opposed to it being hidden because there's nothing to show. Used to
      // offer a "reopen" handle.
      manuallyHidden: false,
      // User-adjustable card height (px). null = use the default CSS height.
      cardHeight: null,
      resizing: false,
      guideState: null
    }
  },

  watch: {
    result(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Defer to nextTick: when ET data arrives the chart is rendered via
        // v-else, so the ref isn't available until the DOM updates.
        this.$nextTick(() => {
          this.updateChart();
          this.updateFovChart();
        });
      }

      // result === null means the selection was explicitly cleared
      // (field deselected / redrawn), so the whole card should close.
      // An empty array ([]) means "ET has no data" — keep the card open if
      // there's still weather to show.
      if (newVal === null) {
        this.hide();
      }
    },
    fetchingResults(newVal) {
      if (newVal === true) {
        // New request starting: default back to the irrigation tab.
        this.activeTabIndex = IRRIGATION_TAB_INDEX;
        this.show();
      } else if (!this.result || !this.result.length) {
        // ET finished with no data. Only hide if there is nothing else
        // worth showing (i.e. no weather). Otherwise keep the card so the
        // weather tab survives and ET tabs show their "no data" text.
        if (this.hasWeatherContent) {
          // ET is empty but weather exists — land the user on the useful tab.
          this.activeTabIndex = WEATHER_TAB_INDEX;
        } else {
          this.hide();
        }
      }
    },
    shouldIgnoreResult(newVal) {
      if (newVal === true && !this.hasWeatherContent) {
        this.hide();
      }
    },
    hasWeatherContent(newVal) {
      // If the only reason the card stayed open was weather, and that weather
      // later disappears (load failed) while ET has no data, close the card.
      if (!newVal && !this.fetchingResults && !(this.result && this.result.length)) {
        this.hide();
      }
    }
  },

  components: {
    apexchart,
    WeatherStrip,
  },

  computed: {
    ...mapState(['fetchingResults', 'result', 'shouldIgnoreResult', 'weather', 'weatherLoading']),
    ...mapGetters(['getIrrigationRatio', 'getPipeSizeRatio']),

    // Weather is independent of ET. If a forecast is present or loading, the
    // card stays open even when ET returns no satellite data, so the Weather
    // tab isn't wiped along with the empty ET plots.
    hasWeatherContent() {
      return this.weatherEnabled &&
        (this.weatherLoading || (Array.isArray(this.weather) && this.weather.length > 0));
    },

    // Whether there is anything worth showing in the card right now. Drives
    // the "reopen" handle so it only appears when the user closed a card that
    // still has content.
    hasAnyContent() {
      return this.fetchingResults || (this.result && this.result.length > 0) || this.hasWeatherContent;
    },

    // Show the small reopen handle only when the user manually closed the card
    // but there is still content to bring back.
    showReopenHandle() {
      return this.manuallyHidden && this.hasAnyContent;
    },

    cardStyle() {
      // Apply a user-dragged height when set; otherwise fall back to CSS.
      return this.cardHeight ? { height: this.cardHeight + 'px' } : {};
    },

    options () {
      return { chart: {
        toolbar: {
            // show: !(this.mobileViewPort),
          tools: {
            customIcons: [{
              icon: '<b-icon icon="chevron-down" style="color: red" aria-hidden="true"></b-icon>',
              index: 4,
              title: 'tooltip of the icon',
              class: 'custom-icon',
              click: () => {
                this.$nextTick(() => {
                  this.handleDownloadSVG();
                });
              }
            }]
          },
          export: { svg: {filename: `chart-${new Date().toISOString().slice(0, 10)}`} },
        },
        id: 'irrigation-values' },
        xaxis: { categories: [] },
        yaxis: { title: { text: this.$i18n.t('global.meter_cube_per_hectare') } } }
    },

    yAxisConfig() {
      let data = this.meanFov.filter(v => v !== null);
      let min = Math.min(...data);
      let max = Math.max(...data);

      return {
        title: { text: this.$i18n.t('global.percent') },
        min: min,
        max: max,
        tickAmount: 5
      };
    },

    fovOptions () {
      return { chart: {
        toolbar: {
          tools: {
            customIcons: [{
              icon: '<b-icon icon="chevron-down" style="color: red" aria-hidden="true"></b-icon>',
              index: 4,
              title: 'tooltip of the icon',
              class: 'custom-icon',
              click: () => {
                this.$nextTick(() => {
                  this.handleDownloadSVG();
                });
              }
            }]
          },
          export: { svg: {filename: `chart-fov-${new Date().toISOString().slice(0, 10)}`} },
        },
        id: 'fov-values' },
        xaxis: { categories: this.dates },
        yaxis: this.yAxisConfig }
    },

    validResult() {
      if (!this.result) return [];
      return this.result.slice(-8);
    },

    dates() {
      if (this.result) {
        return this.validResult.map(value => value[1]);
      } else {
        return [];
      }
    },
    irrigation() {
      if (this.result) {
        return this.validResult.map(value => {
          if (value[0] === null) return null;
          const raw = Number(value[0]);
          const amount = isFinite(raw) ? (parseFloat(raw.toFixed(3) * 10.0) / this.getIrrigationRatio) : 0;
          return amount.toFixed(3);
        });
      } else {
        return [];
      }
    },
    meanFov () {
      if (this.result) {
        return this.validResult.map(value => {
          if (value[2] === null) return null;
          return (value[2].toFixed(3) * 100.0).toFixed(3);
        });
      } else {
        return [];
      }
    },

    irrigationDuration () {
      if (this.result) {
        return this.validResult.map(value => {
          if (value[0] === null) return null;
          const raw = Number(value[0]);
          const amount = isFinite(raw) ? (parseFloat(raw.toFixed(3) * 10.0) / this.getIrrigationRatio) : 0;
          const area = this.field && this.field.fieldArea ? this.field.fieldArea : 0;
          const totalRequirement = (area/10000) * amount;
          const duration = totalRequirement/this.getPipeSizeRatio;
          return duration.toFixed(2);
        });
      } else {
        return [];
      }
    }
  },

  methods: {
    showGuideTab (tabIndex) {
      if (!this.guideState) {
        this.guideState = {
          hideCard: this.hideCard,
          manuallyHidden: this.manuallyHidden,
          activeTabIndex: this.activeTabIndex
        };
      }
      this.activeTabIndex = tabIndex;
      this.show();
    },

    restoreAfterGuide () {
      if (!this.guideState) return;
      this.hideCard = this.guideState.hideCard;
      this.manuallyHidden = this.guideState.manuallyHidden;
      this.activeTabIndex = this.guideState.activeTabIndex;
      this.guideState = null;
    },

    // A day's irrigation can't take more than 24h; beyond that the value is
    // technically correct but meaningless to display, so we show a warning.
    exceedsDailyLimit (durationValue) {
      const n = Number(durationValue);
      return isFinite(n) && n > 24;
    },

    hide () {
      // Automatic hide (no content / cleared selection): not user-initiated,
      // so don't offer the reopen handle.
      this.hideCard = true;
      this.manuallyHidden = false;
    },

    hideManually () {
      // User explicitly closed the card; remember so we can offer a reopen
      // handle while there is still content.
      this.hideCard = true;
      this.manuallyHidden = true;
    },

    show () {
      this.hideCard = false;
      this.manuallyHidden = false;
    },

    startResize (event) {
      this.resizing = true;
      const startY = (event.touches ? event.touches[0].clientY : event.clientY);
      const el = this.$refs.resultsWindow;
      const startHeight = this.cardHeight || (el ? el.offsetHeight : 300);

      const onMove = (e) => {
        // Stop the page from scrolling / rubber-banding while dragging the
        // handle (required on iOS WebView, where touch-action alone is unreliable).
        if (e.cancelable) e.preventDefault();
        const y = (e.touches ? e.touches[0].clientY : e.clientY);
        // Dragging up (smaller y) grows the card; clamp to sane bounds.
        const delta = startY - y;
        const next = startHeight + delta;
        const maxH = Math.round(window.innerHeight * 0.85);
        this.cardHeight = Math.min(Math.max(next, 120), maxH);
      };

      const onUp = () => {
        this.resizing = false;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onUp);
      };

      // Stored on the instance so a beforeDestroy can detach them if the
      // component is ever torn down mid-drag.
      this._onResizeMove = onMove;
      this._onResizeUp = onUp;

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onUp);
    },

    updateFovChart() {
      // The chart is only in the DOM when there is ET data; skip if absent.
      if (!this.$refs.fovChart) return;
      this.$refs.fovChart.updateSeries([{
        name: this.$t('results.mean_fov'),
        data: this.meanFov
      }]);
      this.$refs.fovChart.updateOptions({
        xaxis: {
          categories: this.dates
        }
      });
    },

    updateChart() {
      if (!this.$refs.chart) return;
      this.$refs.chart.updateSeries([{
        name: this.$t('results.irrigation_amount'),
        data: this.irrigation
      }]);
      this.$refs.chart.updateOptions({
        xaxis: {
          categories: this.dates
        }
      });
    },

    handleDownloadSVG() {
      const chartOptions = this.$refs.chart.options;
       new ApexCharts(this.$refs.chart.$el, chartOptions);
      // ctx.exports.exportToSVG(ctx);
      const chartSvg = document.querySelector('#irrigation-values svg');
      const canvas = html2canvas(chartSvg);
      const imgData = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imgData;
      console.log(link)
      link.download = `chart-${new Date().toISOString().slice(0, 10)}.png`;
      link.click();
    },

    swipeHandler (direction) {
      if (direction == 'bottom') {
        document.getElementsByClassName('tabs')[0].style.display = 'none';
      }
    },

    displayResults() {
      document.getElementsByClassName('tabs')[0].style.display = 'block';
    }
  },

  created() {
    // Dashboard asks us to fully close the card when a field is over the 24h
    // limit (infeasible) — the alert is the only thing the user should see.
    this._onHideRequest = () => this.hide();
    this.$root.$on('hideResultsCard', this._onHideRequest);
  },

  beforeDestroy() {
    if (this._onHideRequest) {
      this.$root.$off('hideResultsCard', this._onHideRequest);
    }
    // Safety net: detach any resize listeners if the component is torn down
    // while a drag is in progress.
    if (this._onResizeMove) {
      window.removeEventListener('mousemove', this._onResizeMove);
      window.removeEventListener('touchmove', this._onResizeMove);
    }
    if (this._onResizeUp) {
      window.removeEventListener('mouseup', this._onResizeUp);
      window.removeEventListener('touchend', this._onResizeUp);
    }
  }
};
</script>

<style lang="scss" scoped>
.mr-auto {
  margin-right: auto;
}

// When a custom height is applied, let the card body scroll instead of
// pushing content off-screen.
.results-window {
  display: flex;
  flex-direction: column;
}
.results-card {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

// Vertical resize grab handle along the top edge of the card.
.results-resize-handle {
  height: 16px;
  cursor: ns-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f3f1;
  border-top: 1px solid #d9ded9;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  touch-action: none;
  user-select: none;
  flex: 0 0 auto;
}
.results-resize-handle.resizing,
.results-resize-handle:hover {
  background: #e2e8e2;
}
.results-resize-grip {
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #9aa79a;
}

// Floating handle to reopen the card after it was closed.
.results-reopen-handle {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  padding: 0.35rem 0.9rem;
  background: #526f2f;
  color: #fff;
  border-top-left-radius: 0.6rem;
  border-top-right-radius: 0.6rem;
  cursor: pointer;
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.15);
  font-size: 0.85rem;
}
.results-reopen-handle:hover {
  background: #455d28;
}
// Margin instead of flexbox `gap` (unsupported on iOS < 14.5 / old Android).
.results-reopen-label {
  white-space: nowrap;
  margin-left: 0.4rem;
}
[dir='rtl'] .results-reopen-label {
  margin-left: 0;
  margin-right: 0.4rem;
}

@media (max-width: 576px) {
  .card-header {
    padding-top: 0.4rem;
    padding-bottom: 0.4rem;
  }
}
</style>
